import React from 'react'

import styles from './FeeSheet.module.scss'
import { addPDFViewerParams, combineClasses } from '~/util'
import { ANALYTICS_CATEGORIES } from '~/service'
import { ExternalLinkAction } from '../buttons'

export interface FeeSheetProps extends React.HTMLAttributes<HTMLDivElement> {
  url: string
  /**
   * Whether to apply the responsive styling that adds a border and margin to
   * the iframe above the medium breakpoint. Use this if you don't want to use
   * this component in a full width layout position.
   */
  responsiveStyles?: boolean
}

/**
 * Show an iframe with the community's fee schedule and a linkt to open it in a
 * new tab. The URL will receive PDF viewer parameters to improve the viewing
 * experience if the URL points to a PDF document.
 *
 * If the iframe fails to load, the component will hide itself.
 */
export const FeeSheet = React.memo(
  ({
    url: feeSheetURL,
    responsiveStyles = true,
    className,
    ...rest
  }: FeeSheetProps) => {
    const [hasError, setHasError] = React.useState(false)

    const handleError = React.useCallback(() => {
      setHasError(true)
    }, [])

    if (hasError) {
      return null
    }

    return (
      <div
        data-testid="FeeSheet"
        className={combineClasses(
          styles.FeeSheet,
          responsiveStyles && styles.responsiveFrame,
          className
        )}
        {...rest}
      >
        <iframe
          title="Community Fee Sheet"
          src={addPDFViewerParams(feeSheetURL)}
          className={styles.feeSheetFrame}
          onError={handleError}
        ></iframe>
        <ExternalLinkAction
          data-testid="feeSheetLink"
          className={styles.feeSheetLink}
          href={feeSheetURL}
          category={ANALYTICS_CATEGORIES.DOCUMENT}
          action="Open Fee Sheet"
          label="Fee Sheet"
          blank
          size="s"
          feel="link"
        >
          Open Fee Sheet
        </ExternalLinkAction>
      </div>
    )
  }
)
